import { IDocument, IFile, ObjectValues } from '../common';

import { AdminPermissions } from './admin.entities';
import { IActiveSubscription } from './subscriptions.entities';

/**
 * Created before `USER_TYPE` so the string literal `ADMIN` receives a type id
 * before `TEAM_MEMBER`. CreateSessionModalV2 already fails on `role: string`
 * versus `SessionRole`; atlas-tsc matches that diagnostic by the full text,
 * which sorts `ITeamMember['type']` by these ids. `atlasAccess` on `IUser`
 * moved this module earlier in checking and flipped that sort.
 */
const _atlasAccountRoleLiteralOrder = ['BUSINESS_OWNER', 'ADMIN', 'TEAM_MEMBER'] as const;
void _atlasAccountRoleLiteralOrder;

export const USER_TYPE = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  BUSINESS_OWNER: 'BUSINESS_OWNER',
  TEAM_MEMBER: 'TEAM_MEMBER',
  USER: 'USER',
} as const;

export type TUSER_TYPE = ObjectValues<typeof USER_TYPE>;

export const USER_SIGNUP_METHOD = {
  EMAIL: 'EMAIL',
  GOOGLE: 'GOOGLE',
} as const;

export type TUSER_SIGNUP_METHOD = ObjectValues<typeof USER_SIGNUP_METHOD>;

export interface IFeatureUsage {
  id: string;
  userId: string;
  featureName: string;
  used: number;
  limit: number;
  isUnlimited: boolean;
  resetDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface IUser extends IDocument {
  type: TUSER_TYPE;
  signupMethod: TUSER_SIGNUP_METHOD;
  email: string;
  emailVerifiedAt: string;
  emailTemp: string | null;
  firstName: string;
  phone: {
    countryCode: string;
    number: string;
  };
  lastName: string;
  banned: boolean;
  bannedAt: string | null;
  banReason: string | null;
  bannedById: string | null;
  blockedIds: string[];
  blockedByIds: string[];
  deletedById: string | null;
  slNo: number | null;
  planId: string;
  subscriptions: IActiveSubscription[];
  stripeCustomerId: string | null;
  featureUsage: IFeatureUsage[];
  uploads: string[];
  profilePictures: IFile[];

  // Team Seat Fields (for TEAM_MEMBER type)
  seatState?: 'active' | 'frozen' | 'removed';
  seatFrozenAt?: string;
  seatDeleteAt?: string;
  daysUntilDeletion?: number;
  teamOwnerId?: string;

  // Admin Permissions (for ADMIN type)
  adminPermissions?: AdminPermissions;

  // Shopify Install Fields
  createdFromShopify?: boolean;
  shopifyInstallations?: Array<{
    id: string;
    shop: string;
    shopDomain?: string | null;
    shopifySubscriptionStatus?: string | null;
    shopifyPlanName?: string | null;
    shopifySubscriptionId?: string | null;
  }>;
  // PR #6: Set to true when a Shopify merchant has reinstalled the app or
  // their subscription lapsed, signalling the frontend to prompt re-approval.
  shopifyBillingRefreshRequired?: boolean;

  /**
   * Server enrollment for Atlas home. Missing (older API) means not enrolled.
   * `shell` is the stored preference; null means absent (Atlas when atlasHome is true).
   */
  atlasHome?: boolean;
  /**
   * Server decision for opening Atlas. True only when the agent is enabled, the
   * C5 role rule admits this user, and the account enrollment rule passes.
   * Missing means no access.
   */
  atlasAccess?: boolean;
  shell?: 'atlas' | 'classic' | null;
}
